import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Divider, Link, Paper, Stack, Typography } from '@mui/material';
import { extractMatchedRecord } from './mapper';

import { AgentActivityLogDocumentType } from './query';

const OUTCOME_CONFIG: Record<string, { label: string; color: string; icon: React.ReactElement }> = {
  success: { label: 'Success', color: '#66bb6a', icon: <CheckCircleIcon sx={{ fontSize: 16, color: '#66bb6a' }} /> },
  failed: { label: 'Failed', color: '#ff5252', icon: <ErrorIcon sx={{ fontSize: 16, color: '#ff5252' }} /> },
};

interface SystemInfoSectionProps {
  outcome: string | null | undefined;
  trigger: Record<string, unknown> | null | undefined;
  payload: Record<string, unknown> | null | undefined;
  error?: Record<string, unknown> | null;
  associations?: Record<string, unknown> | null;
  documentType?: string | null;
}

export const SystemInfoSection = ({
  outcome,
  trigger,
  payload,
  error,
  associations,
  documentType,
}: SystemInfoSectionProps) => {
  const outcomeConfig = outcome ? OUTCOME_CONFIG[outcome] : null;
  const isCreditApp = documentType === AgentActivityLogDocumentType.CreditApplication;

  const durationMs = trigger?.duration_milliseconds as number | null | undefined;
  const durationDisplay = durationMs != null ? `${(durationMs / 1000).toFixed(2)} seconds` : null;

  const matchedRecord = extractMatchedRecord(associations ?? null);

  const agentReasoning = payload?.agent_reasoning as Record<string, unknown> | null | undefined;
  const creditSubmissionMatching = agentReasoning?.creditSubmissionMatching as
    | Record<string, unknown>
    | null
    | undefined;

  const matchScore = !isCreditApp ? (creditSubmissionMatching?.score as number | null | undefined) : null;
  const matchConfidenceDisplay =
    matchScore != null ? `${Math.round(matchScore * 100)}% - ${matchScore >= 0.7 ? 'High' : 'Low'}` : 'None';
  const isHighConfidence = matchScore != null && matchScore >= 0.7;

  const matchType = !isCreditApp ? (creditSubmissionMatching?.matchType as string | null | undefined) : null;
  const matchTypeDisplay = matchType ? `${matchType.charAt(0).toUpperCase()}${matchType.slice(1)} Match` : 'None';

  const reasoningItems: string[] = [];
  if (!isCreditApp) {
    const creditReasoning = creditSubmissionMatching?.reasoning as string | null | undefined;
    if (creditReasoning) reasoningItems.push(creditReasoning);
    const lenderReasoning = (agentReasoning?.lenderNameAnalysis as Record<string, unknown> | null | undefined)
      ?.reasoning as string | null | undefined;
    if (lenderReasoning) reasoningItems.push(lenderReasoning);
    const dealReasoning = (agentReasoning?.dealNameAnalysis as Record<string, unknown> | null | undefined)
      ?.reasoning as string | null | undefined;
    if (dealReasoning) reasoningItems.push(dealReasoning);
  }

  const errorMessages = error?.messages as string[] | null | undefined;
  if (Array.isArray(errorMessages)) {
    errorMessages.forEach(msg => {
      if (msg) reasoningItems.push(msg);
    });
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        System Info
      </Typography>
      <Stack>
        {outcomeConfig && (
          <>
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                Outcome
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                {outcomeConfig.icon}
                <Typography variant="body2" sx={{ color: outcomeConfig.color, fontWeight: 500 }}>
                  {outcomeConfig.label}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
          </>
        )}

        {durationDisplay && (
          <>
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                Duration
              </Typography>
              <Typography variant="body2">{durationDisplay}</Typography>
            </Stack>
            <Divider />
          </>
        )}

        <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
            Associated Record
          </Typography>
          {matchedRecord?.url ? (
            <Link href={matchedRecord.url} target="_blank" rel="noopener noreferrer" variant="body2">
              {matchedRecord.label}
            </Link>
          ) : (
            <Typography
              variant="body2"
              sx={!matchedRecord ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
            >
              {matchedRecord?.label ?? 'Record Not Found'}
            </Typography>
          )}
        </Stack>

        <Divider />

        {!isCreditApp && (
          <>
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                Match Confidence
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: matchScore != null ? (isHighConfidence ? '#4caf50' : '#ffa726') : 'inherit' }}
              >
                {matchConfidenceDisplay}
              </Typography>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                Match Type
              </Typography>
              <Typography variant="body2">{matchTypeDisplay}</Typography>
            </Stack>
          </>
        )}

        {reasoningItems.length > 0 && (
          <>
            <Divider />
            <Stack direction="row" spacing={1} sx={{ py: 0.75 }} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                Reasoning
              </Typography>
              <Stack spacing={1}>
                {reasoningItems.map((item, index) => (
                  <Stack key={index} direction="row" spacing={0.75} alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                      •
                    </Typography>
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
